import { LoadingOutlined } from "@ant-design/icons";
import { PlayCircleIcon } from "@phosphor-icons/react";
import { Flex, Grid, Spin } from "antd";
import { useAntTheme } from "hooks/useAntTheme/";
import { useState } from "react";
import { VideoMetadata } from "./types";
import { radius } from "@libertify/ui";

export interface ProjectVideoProps {
  videoUrl: string;
  backgroundImg: string;
  videoMetadata: VideoMetadata;
  videoRef: React.RefObject<HTMLVideoElement>;
}

export const ProjectVideo = ({
  videoUrl,
  backgroundImg,
  videoMetadata,
  videoRef,
}: ProjectVideoProps) => {
  const breakpoint = Grid.useBreakpoint();
  const [isPlaying, setIsPlaying] = useState(false);
  const { theme } = useAntTheme();

  const handlePlay = () => {
    setIsPlaying(true);
    if (videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.error("Error playing video:", error);
      });
    }
  };

  const commonContentStyle = {
    width: breakpoint.md
      ? `min(calc(${videoMetadata?.aspectRatio || 16 / 9} * clamp(200px, 50vh, 600px)), 1000px)`
      : "100%", // On mobile, use full width to avoid stretching
    maxWidth: "100%",
    height: "fit-content",
  };

  return (
    <div
      style={{
        border: "6px solid",
        borderColor: theme.colorOutlineBorder,
        borderRadius: radius.box,
        display: "inline-block",
        overflow: "hidden",
        lineHeight: 0, // removes extra space below video
        position: "relative", // Required for absolute positioning of play icon
        padding: 0, // No padding
        boxSizing: "border-box",
        backgroundImage: isPlaying ? "none" : `url(${backgroundImg})`,
        backgroundColor: "#000",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {videoUrl ? (
        <video
          ref={videoRef}
          style={{
            ...commonContentStyle,
            objectFit: "contain", // Preserve aspect ratio to prevent stretching
            display: "block", // Remove any default spacing
            margin: 0,
            padding: 0,
            backgroundColor: "#000", // Match container background
          }}
          preload="metadata"
          controls
          autoPlay
          muted
          src={videoUrl}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        >
          <track kind="captions" src="" />
        </video>
      ) : (
        <Flex
          justify="center"
          align="center"
          style={{
            ...commonContentStyle,
            aspectRatio: videoMetadata?.aspectRatio || 16 / 9,
            overflow: "hidden",
            backgroundColor: theme.colorBgLayout,
          }}
        >
          <Spin indicator={<LoadingOutlined style={{ fontSize: 60 }} spin />} />
        </Flex>
      )}

      {!isPlaying && videoUrl && (
        <PlayCircleIcon
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 10,
            fontSize: "clamp(50px, 8vw, 70px)",
            color: theme.colorWhite,
            cursor: "pointer",
          }}
          onClick={handlePlay}
        />
      )}
    </div>
  );
};
